import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { Navigate } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import ComponentDetail from './ComponentDetail';
import IconGallery from './IconGallery';
import ColorGallery from './ColorGallery';
import TokenGallery from './TokenGallery';
import LibraryNav from './LibraryNav';
import OnThisPage, { SCROLL_MARGIN_TOP } from './OnThisPage';
import { normalizeCatalogComponent } from './sandpackMapper';
import { groupCatalogComponents } from './catalogGrouping';
import { docs } from './docsStyles';

// Lazy-load the playground so Sandpack (~2.5 MB) ships in its own chunk and never
// weighs on PM Web's initial bundle (plan R6 / T5.5). It loads only when this section
// is opened and a component is selected.
const LibraryPlayground = lazy(() => import('./LibraryPlayground'));

// Merge / decorate design-tokens entries so they render as example-driven galleries
// instead of raw value dumps. Pairs (Colors+DarkColors, cardShadow+itemShadow,
// Fonts+FontStacks) collapse into one entry; the partner is hidden from the list.
// Requires resolved `values` (producer feedback F7); without them entries fall back to the
// in-sandbox utility viewer. Returns a new list with hidden partners removed.
function decorateDesignTokens(flat) {
  const isDT = c => (c.package || '').includes('design-tokens');
  const find = name => flat.find(c => c.name === name && isDT(c));
  const hide = new Set();

  const colors = find('Colors');
  const darkColors = find('DarkColors');
  if (colors && colors.values) {
    colors.isColorGallery = true;
    colors.lightColors = colors.values;
    colors.darkColors = darkColors?.values || null;
    if (darkColors) hide.add(darkColors);
  }

  const cardShadow = find('cardShadow');
  const itemShadow = find('itemShadow');
  if (cardShadow && cardShadow.values) {
    cardShadow.name = 'Shadows';
    cardShadow.tokenKind = 'shadows';
    cardShadow.tokenData = [
      { name: 'cardShadow', token: cardShadow.values },
      ...(itemShadow?.values ? [{ name: 'itemShadow', token: itemShadow.values }] : []),
    ];
    if (itemShadow) hide.add(itemShadow);
  }

  const fonts = find('Fonts');
  const fontStacks = find('FontStacks');
  if (fonts && fonts.values) {
    fonts.tokenKind = 'fonts';
    fonts.tokenData = { weights: fonts.values, stacks: fontStacks?.values || null };
    if (fontStacks) hide.add(fontStacks);
  }

  const radii = find('Radii');
  if (radii && radii.values) {
    radii.tokenKind = 'radii';
    radii.tokenData = radii.values;
  }
  const spacing = find('Spacing');
  if (spacing && spacing.values) {
    spacing.tokenKind = 'spacing';
    spacing.tokenData = spacing.values;
  }

  return flat.filter(c => !hide.has(c));
}

// Merge the small Tremor primitives that all live in `tremor/Tools.js` into compact SHOWCASE
// entries (one playground rendering many components in [name | rendered | copy] rows) instead
// of a giant box per tiny component. "Badges" is split out; the rest go to "Text & primitives".
function mergeToolsShowcase(flat) {
  const TOOLS_ENTRY = '/lib/tremor/Tools.js';
  const members = flat.filter(component => component.entry === TOOLS_ENTRY);
  if (members.length === 0) return flat;

  const isBadge = component => component.name === 'Badge' || component.name === 'BadgeDelta';
  const base = members[0];
  const sharedFiles = members.reduce((acc, c) => ({ ...acc, ...(c.files || {}) }), {});
  const sharedDeps = members.reduce((acc, c) => ({ ...acc, ...(c.dependencies || {}) }), {});

  const makeShowcase = (name, description, group, order) => ({
    name,
    description,
    package: base.package,
    sandboxType: base.sandboxType,
    categoryId: base.categoryId,
    categoryLabel: base.categoryLabel,
    categoryOrder: base.categoryOrder,
    order,
    props: [],
    entry: TOOLS_ENTRY,
    files: sharedFiles,
    dependencies: sharedDeps,
    showcaseMembers: group.map(c => ({
      name: c.name,
      localName: c.localName || c.name,
      examples: (Array.isArray(c.examples) ? c.examples : []).map(example => example.props || {}),
    })),
  });

  const showcases = [];
  const textMembers = members.filter(c => !isBadge(c));
  const badgeMembers = members.filter(isBadge);
  if (textMembers.length) {
    showcases.push(makeShowcase('Text & primitives', 'Tremor text and layout primitives.', textMembers, -2));
  }
  if (badgeMembers.length) {
    showcases.push(makeShowcase('Badges', 'Tremor badge components and variants.', badgeMembers, -1));
  }

  return [...showcases, ...flat.filter(component => component.entry !== TOOLS_ENTRY)];
}

// InternalLibrariesView — wired to the Redux store via the InternalLibraries container.
// Three-column docs layout (MUI-docs style): grouped navigation on the left, component
// detail + playground in the center, a floating "On this page" index on the right.
//
// States: unauthorized / loading / error / empty / ready.
function InternalLibrariesView({ catalog, loading, isAdmin, fetchInternalLibraryCatalog, t }) {
  // Flatten + normalize, tagging each component with its package, sandboxType and the
  // resolved category (label/order looked up from the package's categories — catalog v2).
  const components = useMemo(() => {
    const pkgs = catalog?.packages || [];
    const flat = pkgs.flatMap(pkg => {
      const categoryById = new Map((pkg.categories || []).map(category => [category.id, category]));
      return (pkg.components || []).map(component => {
        const category = categoryById.get(component.category);
        return {
          ...normalizeCatalogComponent(component),
          package: pkg.package,
          sandboxType: pkg.sandboxType,
          categoryId: component.category,
          categoryLabel: category?.label,
          categoryOrder: category?.order,
          order: component.order,
        };
      });
    });

    return mergeToolsShowcase(decorateDesignTokens(flat));
  }, [catalog]);

  const [search, setSearch] = useState('');
  const [selectedName, setSelectedName] = useState(null);

  // Free-text filter over name + description (plan T3.2).
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return components;
    return components.filter(
      component =>
        component.name.toLowerCase().includes(query) ||
        (component.description || '').toLowerCase().includes(query)
    );
  }, [components, search]);

  // Group filtered components by package → family for the left nav (plan T3.1).
  const groups = useMemo(() => groupCatalogComponents(filtered), [filtered]);

  // Keep the selection valid: if the current selection is filtered out (or none yet),
  // select the first available component. Selection key is package/name for uniqueness.
  useEffect(() => {
    if (filtered.length === 0) return;
    if (!filtered.some(c => `${c.package}/${c.name}` === selectedName)) {
      setSelectedName(`${filtered[0].package}/${filtered[0].name}`);
    }
  }, [filtered, selectedName]);

  // Trigger the API fetch on mount (container injects fetchInternalLibraryCatalog).
  useEffect(() => {
    if (fetchInternalLibraryCatalog) fetchInternalLibraryCatalog();
  }, [fetchInternalLibraryCatalog]);

  const selected = filtered.find(c => `${c.package}/${c.name}` === selectedName);
  const isIconGallery = Array.isArray(selected?.icons) && selected.icons.length > 0;
  const isColorGallery = Boolean(selected?.isColorGallery);
  const tokenKind = selected?.tokenKind || null;

  // --- UI state: unauthorized ---
  // isAdmin===undefined means the session hasn't loaded yet — don't redirect.
  // Only redirect once we know for certain the user is not an admin.
  if (isAdmin === false) {
    return <Navigate to="/" replace />;
  }

  // --- UI state: loading ---
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 4 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {t('internalLibraries.loading', 'Loading catalog…')}
        </Typography>
      </Box>
    );
  }

  // --- UI state: error (catalog unavailable after fetch) ---
  if (!catalog) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
          {t('internalLibraries.page_title', 'Internal Libraries')}
        </Typography>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => fetchInternalLibraryCatalog && fetchInternalLibraryCatalog()}>
              {t('internalLibraries.error_retry', 'Retry')}
            </Button>
          }>
          {t('internalLibraries.error_title', 'Could not load the catalog')}
        </Alert>
      </Box>
    );
  }

  // --- UI state: empty (no components in the whole catalog) ---
  if (components.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
          {t('internalLibraries.page_title', 'Internal Libraries')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('internalLibraries.empty', 'No components in catalog.')}
        </Typography>
      </Box>
    );
  }

  // --- UI state: ready ---
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', fontFamily: docs.font, color: docs.text }}>
      <Grid container spacing={4}>
        {/* Left rail — catalog title + search + grouped navigation */}
        <Grid item xs={12} md={3}>
          <LibraryNav
            title={t('internalLibraries.page_title', 'Internal Libraries')}
            subtitle={t('internalLibraries.catalog_meta', {
              defaultValue: 'catalog version {{version}} · generated {{generatedAt}}',
              version: catalog.version,
              generatedAt: catalog.generatedAt,
            })}
            groups={groups}
            selectedName={selectedName}
            onSelect={setSelectedName}
            search={search}
            onSearchChange={setSearch}
            t={t}
          />
        </Grid>

        {/* Center — component detail + playground */}
        <Grid item xs={12} md={9} lg={6}>
          {selected ? (
            <ComponentDetail
              component={selected}
              t={t}
              playgroundSlot={
                <Box id="playground" sx={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px`, mb: 4 }}>
                  <Typography
                    sx={{
                      fontFamily: docs.font,
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      color: docs.textFaint,
                      mb: 1.25,
                    }}>
                    {isColorGallery
                      ? t('internalLibraries.colors_gallery_title', 'Colors')
                      : tokenKind
                      ? t('internalLibraries.tokens_examples_title', 'Examples')
                      : isIconGallery
                      ? t('internalLibraries.gallery_title', 'Icon gallery')
                      : t('internalLibraries.playground_title', 'Playground')}
                  </Typography>
                  {isColorGallery ? (
                    <ColorGallery light={selected.lightColors} dark={selected.darkColors} t={t} />
                  ) : tokenKind ? (
                    <TokenGallery kind={tokenKind} data={selected.tokenData} t={t} />
                  ) : isIconGallery ? (
                    <IconGallery component={selected} t={t} />
                  ) : (
                    <Suspense
                      fallback={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            {t('internalLibraries.playground_loading', 'Loading playground…')}
                          </Typography>
                        </Box>
                      }>
                      <LibraryPlayground key={selected.name} entry={selected} t={t} />
                    </Suspense>
                  )}
                </Box>
              }
            />
          ) : (
            <Typography variant="body2">{t('internalLibraries.no_results', 'No components match your search.')}</Typography>
          )}
        </Grid>

        {/* Right rail — floating "On this page" index (hidden below lg) */}
        <Grid item lg={3} sx={{ display: { xs: 'none', lg: 'block' } }}>
          {selected && <OnThisPage key={selected.name} t={t} />}
        </Grid>
      </Grid>
    </Box>
  );
}

export default withTranslation()(InternalLibrariesView);
